import React, { useEffect, useState } from 'react';
import ColorToolIcon from '../../colorTools/ColorToolIcon';
import { ToolbarItemConfig } from '../../../../toolbarTypes';
import { useDispatch, useSelector } from 'react-redux';
import { activePopupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';
import ToolbarPopupTool from '../../../ToolbarPopupTool';
import { getSelectedLabelId, selectUsableLabels } from '../../../../../../element/labels/labelSelectors';
import DesktopColorPopup from '../../colorTools/popup/desktop/DesktopColorPopup';
import {
    getColorDefaultHexValue,
    getColorDisplayValue,
} from '../../../../../../../common/colors/elementColorFormatUtil';
import { DEFAULT_ELEMENT_COLOR, DEFAULT_LABEL_COLORS } from '../../../../../../../common/colors/colorConstants';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import { COLOR_POPUP_MODE } from '../../colorTools/popup/colorPopupConstants';
import Immutable from 'immutable';
import { ElementType } from '../../../../../../../common/elements/elementTypes';
import {
    clearTemporaryLabelColorStyles,
    setTemporaryLabelColorStyles,
} from '../../colorTools/setTemporaryLabelColorStyles';

const MOCK_LABEL_ELEMENT = Immutable.fromJS({
    elementType: ElementType.ANNOTATION_TYPE,
});

type LabelColorToolProps = {
    item: ToolbarItemConfig;
};

const LabelColorTool: React.FC<LabelColorToolProps> = (props: LabelColorToolProps) => {
    const { item } = props;

    const dispatch = useDispatch();
    const selectedLabelId = useSelector(getSelectedLabelId);
    const labels = useSelector(selectUsableLabels);
    const activePopup = useSelector(activePopupSelector);
    const [tempColor, setTempColor] = useState<string | null>(null);

    const popupId = item?.uid || PopupIds.LABEL_TOOL_COLOR;
    const isPopupOpen = activePopup.has(popupId);
    const selectedLabel = selectedLabelId ? labels?.[selectedLabelId] : undefined;

    useEffect(() => {
        if (!selectedLabel) return;

        if (!isPopupOpen) {
            clearTemporaryLabelColorStyles(selectedLabel.id);
            setTempColor(null);
            return;
        }

        return () => {
            clearTemporaryLabelColorStyles(selectedLabel.id);
        };
    }, [isPopupOpen, selectedLabel?.id]);

    if (!selectedLabel) return null;

    const handleTempColorChange = (color: string | null) => {
        if (!selectedLabel || !color) return;

        setTempColor(color);
        setTemporaryLabelColorStyles(selectedLabel.id, color);
    };

    const handleColorChange = (color: string | null) => {
        if (!selectedLabel) return;

        const colorToSave = color ? getColorDefaultHexValue(color) : undefined;
        dispatch(updateLabel(selectedLabel.id, { color: colorToSave }));
        clearTemporaryLabelColorStyles(selectedLabel.id);
        setTempColor(null);
    };

    const labelColor = tempColor ?? selectedLabel.color;
    const labelColorHex = getColorDefaultHexValue(labelColor) || DEFAULT_ELEMENT_COLOR;
    const labelColorDisplayValue = getColorDisplayValue(labelColor);

    const popupProps = {
        selectedColor: labelColor,
        selectedColorHex: labelColorHex,
        selectedBackgroundColor: null,
        selectedBackgroundColorHex: null,
        supportDualColors: false,
        elementType: 'label',
        customColorHandler: handleColorChange,
        customTempColorHandler: handleTempColorChange,
        colorPopupMode: COLOR_POPUP_MODE.COLOR,
        defaultColors: DEFAULT_LABEL_COLORS,
        selectedElements: Immutable.List([MOCK_LABEL_ELEMENT]),
    };

    return (
        <ToolbarPopupTool
            {...props}
            {...popupProps}
            toolName="label-color"
            popupId={popupId}
            buttonSelector=".Label-colorTool .ColorToolIcon"
            IconComponent={() => (
                <ColorToolIcon
                    key={`label-color-${selectedLabel.id}`}
                    supportColorBar={false}
                    supportBackgroundColor={false}
                    selectedType={undefined}
                    color={labelColorDisplayValue}
                    isActive={isPopupOpen}
                />
            )}
            PopupComponent={DesktopColorPopup}
        />
    );
};

export default LabelColorTool;
